/**
 * Revenue dashboard helpers and display constants.
 * Invoice data is loaded from GET /admin/revenue/invoices (library + BLU + editable tracking).
 */

/** Uppercase labels aligned with BLU / scope PDF service titles */
export const SERVICE_TYPE_LABELS = {
  brand_design: "BRAND DESIGN",
  web_design: "WEB DESIGN",
  product_design: "PRODUCT DESIGN",
  operational_ecosystem: "OPERATIONAL ECOSYSTEM",
};

/** Canonical service titles offered on invoices (dropdown options). */
export const SERVICE_OFFERING_TITLES = [
  SERVICE_TYPE_LABELS.operational_ecosystem,
  SERVICE_TYPE_LABELS.brand_design,
  SERVICE_TYPE_LABELS.web_design,
  SERVICE_TYPE_LABELS.product_design,
];

/**
 * Client display name → service_tier (same keys as portal `clients.service_tier`).
 * Update when a client's Type changes in admin, or when onboarding a new client.
 */
export const CLIENT_SERVICE_TIER = {
  "Flower Children World": "operational_ecosystem",
  "Studio 7 Miami": "operational_ecosystem",
  "Say Sukii": "operational_ecosystem",
};

/** Newest → oldest for “By client” cards */
export const CLIENT_DISPLAY_ORDER = [
  "Flower Children World",
  "Studio 7 Miami",
  "Say Sukii",
];

export function invoiceTypeForClient(clientName) {
  const tier = CLIENT_SERVICE_TIER[clientName];
  return SERVICE_TYPE_LABELS[tier] || SERVICE_TYPE_LABELS.operational_ecosystem;
}

export const PAYMENT_PLAN_LABELS = {
  full: "Pay in full",
  partial: "Partial payments",
};

/** @returns {"full"|"partial"} */
export function normalizePaymentPlan(plan) {
  if (plan === "deposit_50_50" || plan === "partial") return "partial";
  return "full";
}

export function isPartialPlan(plan) {
  return normalizePaymentPlan(plan) === "partial";
}

export const INVOICE_STATUSES = ["DRAFT", "DUE", "PAID", "PARTIAL", "OUTSTANDING", "OVERDUE"];

/** Statuses the admin can set manually (date-based statuses are computed). */
export const MANUAL_INVOICE_STATUSES = ["DRAFT"];

export function invoiceCollected(row) {
  if (row?.collected != null) return Number(row.collected) || 0;
  const deposits = row?.deposits || [];
  return deposits.reduce((sum, d) => sum + (Number(d.paid_amount) || 0), 0);
}

/** Latest deposit paid_at when invoice is PAID (or any paid activity). */
export function invoicePaidAt(row) {
  if (row?.paidAt) return row.paidAt;
  const deposits = row?.deposits || [];
  let latest = null;
  for (const d of deposits) {
    if ((Number(d.paid_amount) || 0) <= 0) continue;
    const at = (d.paid_at || "").slice(0, 10);
    if (!at) continue;
    if (!latest || at > latest) latest = at;
  }
  return latest;
}

export function invoiceOutstanding(row) {
  const amount = Number(row?.amount) || 0;
  return Math.max(0, amount - invoiceCollected(row));
}

export function matchesStatusFilter(row, filterKey) {
  if (filterKey === "all") return true;
  if (filterKey === "OUTSTANDING") {
    return ["OUTSTANDING", "PARTIAL", "OVERDUE", "DUE"].includes(row.status);
  }
  if (filterKey === "DRAFT") {
    return row.status === "DRAFT";
  }
  return row.status === filterKey;
}

/** Minimum px per month on the collected chart (enables horizontal scroll on narrow viewports) */
export const CHART_MONTH_SLOT_PX = 72;

export function invoiceIssuedYear(row) {
  if (!row?.issued) return null;
  return parseIsoDate(row.issued).getFullYear();
}

/** Years that have at least one invoice, newest first */
export function getAvailableYears(invoices) {
  const years = new Set();
  for (const row of invoices || []) {
    const y = invoiceIssuedYear(row);
    if (y != null) years.add(y);
  }
  return Array.from(years).sort((a, b) => b - a);
}

export function filterInvoicesByYear(invoices, year) {
  return (invoices || []).filter((row) => invoiceIssuedYear(row) === year);
}

export function defaultRevenueYear(invoices) {
  const available = getAvailableYears(invoices);
  const current = new Date().getFullYear();
  if (available.includes(current)) return current;
  return available[0] ?? current;
}

export function formatUsd(amount) {
  const n = Number(amount) || 0;
  const hasCents = Math.round(Math.abs(n) * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatUsdCompact(amount) {
  if (amount >= 1000) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(amount);
  }
  return formatUsd(amount);
}

function collectedInMonthFromDeposits(row, y, month) {
  let total = 0;
  for (const d of row.deposits || []) {
    const paid = Number(d.paid_amount) || 0;
    if (paid <= 0 || !d.paid_at) continue;
    const dt = parseIsoDate(d.paid_at);
    if (dt.getFullYear() === y && dt.getMonth() === month) total += paid;
  }
  return total;
}

export function getRevenueMetrics(invoices, year) {
  const list = filterInvoicesByYear(invoices, year);
  const totalInvoiced = list.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalCollected = list.reduce((sum, row) => sum + invoiceCollected(row), 0);
  const outstanding = list.reduce((sum, row) => sum + invoiceOutstanding(row), 0);

  const now = new Date();
  let mrr = 0;
  let mrrSub = "Collected this month";

  if (year === now.getFullYear()) {
    mrr = list.reduce(
      (sum, row) => sum + collectedInMonthFromDeposits(row, now.getFullYear(), now.getMonth()),
      0,
    );
  } else {
    for (let month = 11; month >= 0; month -= 1) {
      const monthTotal = list.reduce(
        (sum, row) => sum + collectedInMonthFromDeposits(row, year, month),
        0,
      );
      if (monthTotal > 0) {
        mrr = monthTotal;
        mrrSub = `Last collected month (${new Date(year, month, 1).toLocaleDateString("en-US", { month: "short" })})`;
        break;
      }
    }
  }

  return { totalInvoiced, totalCollected, outstanding, mrr, mrrSub, year };
}

/** Paid revenue by month: full calendar year (January–December). */
export function getMonthlyCollectedSeries(invoices, year) {
  const list = invoices || [];
  const start = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  const months = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    const y = cursor.getFullYear();
    const month = cursor.getMonth();
    const key = `${y}-${String(month + 1).padStart(2, "0")}`;
    const total = list.reduce((sum, row) => sum + collectedInMonthFromDeposits(row, y, month), 0);

    months.push({
      key,
      label: cursor.toLocaleDateString("en-US", { month: "short" }),
      total,
    });
    cursor = new Date(y, month + 1, 1);
  }

  return months;
}

export function monthlyCollectedCaption(year) {
  return `January — December ${year}`;
}

export function getClientBreakdown(invoices) {
  const map = new Map();
  for (const row of invoices || []) {
    const name = row.client || "Unknown";
    if (!map.has(name)) {
      map.set(name, { client: name, invoiced: 0, collected: 0, outstanding: 0 });
    }
    const entry = map.get(name);
    entry.invoiced += row.amount || 0;
    entry.collected += invoiceCollected(row);
    entry.outstanding += invoiceOutstanding(row);
  }

  const ordered = CLIENT_DISPLAY_ORDER.map((name) => map.get(name)).filter(Boolean);
  const rest = Array.from(map.values()).filter(
    (row) => !CLIENT_DISPLAY_ORDER.includes(row.client),
  );
  return [...ordered, ...rest];
}

export function formatDisplayDate(iso) {
  if (!iso) return "—";
  return parseIsoDate(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function parseIsoDate(iso) {
  return new Date(`${iso}T12:00:00`);
}

export function roundMoney(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

export function addDaysIso(isoDate, days) {
  const base = parseIsoDate((isoDate || "").slice(0, 10));
  base.setDate(base.getDate() + days);
  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, "0");
  const d = String(base.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function newDepositId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `dep-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Mirror backend build_deposits for invoice edit UI. */
export function buildDepositsForPlan(amount, plan, issued, due, { prefillPaidDates = false } = {}) {
  const amt = roundMoney(amount);
  const issuedDate = (issued || "").slice(0, 10) || new Date().toISOString().slice(0, 10);
  const dueDate = (due || issuedDate).slice(0, 10);

  if (normalizePaymentPlan(plan) === "partial") {
    const half = roundMoney(amt / 2);
    const remainder = roundMoney(amt - half);
    return [
      {
        id: newDepositId(),
        label: "Payment 1",
        percent: 50,
        amount: half,
        due: issuedDate,
        paid_amount: prefillPaidDates ? half : 0,
        paid_at: prefillPaidDates ? issuedDate : null,
      },
      {
        id: newDepositId(),
        label: "Payment 2",
        percent: 50,
        amount: remainder,
        due: dueDate,
        paid_amount: 0,
        paid_at: prefillPaidDates ? dueDate : null,
      },
    ];
  }

  return [
    {
      id: newDepositId(),
      label: "Full payment",
      percent: 100,
      amount: amt,
      due: dueDate,
      paid_amount: 0,
      paid_at: null,
    },
  ];
}

/** Align deposit rows with payment plan (adds 50/50 rows, preserves ids + payments). */
export function syncDepositsToPaymentPlan(prevDeposits, amount, plan, issued, due) {
  const normalized = normalizePaymentPlan(plan);
  const parsed = roundMoney(amount);
  const issuedDate = (issued || "").slice(0, 10);
  const dueDate = (due || issuedDate).slice(0, 10);
  const prev = prevDeposits || [];

  const needsRebuild =
    (normalized === "partial" && prev.length < 2) ||
    (normalized === "full" && prev.length !== 1);

  if (!needsRebuild && normalized === "partial" && prev.length >= 2) {
    return rescaleDepositsForPlan(prev, parsed, normalized, issuedDate);
  }

  const prefillPaidDates = normalized === "partial";
  const built = buildDepositsForPlan(parsed, normalized, issuedDate, dueDate, { prefillPaidDates });

  return built.map((d, i) => ({
    ...d,
    id: prev[i]?.id ?? d.id,
    paid_amount:
      prefillPaidDates && i === 0
        ? d.amount
        : prefillPaidDates && i === 1
          ? prev[1]?.paid_amount ?? 0
          : prev[i]?.paid_amount ?? 0,
    paid_at:
      prev[i]?.paid_at ||
      (prefillPaidDates ? d.paid_at : null) ||
      null,
  }));
}

/** Split invoice total evenly across partial payment rows (keeps ids + paid fields). */
export function redistributePartialAmounts(deposits, invoiceAmount) {
  const rows = deposits || [];
  const n = rows.length;
  if (n === 0) return rows;
  const total = roundMoney(invoiceAmount);
  const each = roundMoney(total / n);
  let allocated = 0;
  return rows.map((d, i) => {
    const amount = i < n - 1 ? each : roundMoney(total - allocated);
    if (i < n - 1) allocated += amount;
    const percent = total > 0 ? roundMoney((amount / total) * 100) : 0;
    return {
      ...d,
      label: d.label || `Payment ${i + 1}`,
      amount,
      percent,
    };
  });
}

export function addPartialDeposit(deposits, invoiceAmount, issued) {
  const issuedDate = (issued || "").slice(0, 10);
  const index = (deposits || []).length;
  const next = [
    ...(deposits || []),
    {
      id: newDepositId(),
      label: `Payment ${index + 1}`,
      percent: 0,
      amount: 0,
      due: addDaysIso(issuedDate, 15 * index),
      paid_amount: 0,
      paid_at: null,
    },
  ];
  return redistributePartialAmounts(next, invoiceAmount);
}

export function removePartialDeposit(deposits, depositId, invoiceAmount) {
  const filtered = (deposits || []).filter((d) => d.id !== depositId);
  if (filtered.length < 2) return deposits || [];
  return redistributePartialAmounts(
    filtered.map((d, i) => ({ ...d, label: `Payment ${i + 1}` })),
    invoiceAmount,
  );
}

/** Whether a deposit row is settled (has a paid date or paid covers scheduled). */
export function isDepositSettled(dep) {
  const paid = Number(dep?.paid_amount) || 0;
  if (paid <= 0) return false;
  if (dep?.paid_at) return true;
  const amt = Number(dep?.amount) || 0;
  return paid + 0.001 >= amt;
}

/**
 * Align schedule with real payments:
 * - settled rows use paid_amount as their schedule amount
 * - unpaid rows share the remaining invoice balance (last unpaid gets remainder)
 */
export function reconcileDepositSchedule(deposits, invoiceTotal) {
  const total = roundMoney(invoiceTotal);
  if (!deposits?.length) return deposits || [];

  const out = deposits.map((d) => ({ ...d }));
  const unpaidIdxs = [];
  let paidSum = 0;

  for (let i = 0; i < out.length; i += 1) {
    const paid = roundMoney(Number(out[i].paid_amount) || 0);
    if (isDepositSettled(out[i]) && paid > 0) {
      out[i] = {
        ...out[i],
        amount: paid,
        paid_amount: paid,
      };
      paidSum = roundMoney(paidSum + paid);
    } else {
      unpaidIdxs.push(i);
    }
  }

  const remaining = roundMoney(Math.max(0, total - paidSum));

  if (unpaidIdxs.length === 0) {
    // All installments look settled, but invoice balance remains (e.g. full-plan
    // partial collection). Keep outstanding on the last row.
    if (remaining > 0 && out.length) {
      const last = out[out.length - 1];
      const lastPaid = roundMoney(Number(last.paid_amount) || 0);
      out[out.length - 1] = {
        ...last,
        amount: roundMoney(lastPaid + remaining),
      };
    }
    return out.map((d) => ({
      ...d,
      percent: total > 0 ? roundMoney(((Number(d.amount) || 0) / total) * 100) : 0,
    }));
  }

  if (unpaidIdxs.length === 1) {
    const idx = unpaidIdxs[0];
    out[idx] = { ...out[idx], amount: remaining };
  } else {
    const oldUnpaidSum = roundMoney(
      unpaidIdxs.reduce((s, i) => s + (Number(out[i].amount) || 0), 0),
    );
    let allocated = 0;
    unpaidIdxs.forEach((idx, j) => {
      if (j < unpaidIdxs.length - 1) {
        const share =
          oldUnpaidSum > 0
            ? roundMoney(remaining * ((Number(out[idx].amount) || 0) / oldUnpaidSum))
            : roundMoney(remaining / unpaidIdxs.length);
        out[idx] = { ...out[idx], amount: share };
        allocated = roundMoney(allocated + share);
      } else {
        out[idx] = { ...out[idx], amount: roundMoney(remaining - allocated) };
      }
    });
  }

  return out.map((d) => ({
    ...d,
    percent: total > 0 ? roundMoney(((Number(d.amount) || 0) / total) * 100) : 0,
  }));
}

/** Add invoice total increase onto unpaid remaining (keeps fully-paid rows fixed). */
export function applyAmountIncreaseToRemaining(deposits, oldAmount, newAmount, plan, issued) {
  const normalized = normalizePaymentPlan(plan);
  const prev = roundMoney(oldAmount);
  const next = roundMoney(newAmount);
  const delta = roundMoney(next - prev);
  if (delta <= 0 || !deposits?.length) {
    return reconcileDepositSchedule(
      rescaleDepositsForPlan(deposits, next, normalized, issued),
      next,
    );
  }

  const out = deposits.map((d) => ({ ...d }));
  let targetIdx = -1;
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (!isDepositSettled(out[i])) {
      targetIdx = i;
      break;
    }
  }
  if (targetIdx < 0) targetIdx = out.length - 1;

  out[targetIdx] = {
    ...out[targetIdx],
    amount: roundMoney((Number(out[targetIdx].amount) || 0) + delta),
    label:
      out[targetIdx].label ||
      (normalized === "full" ? "Full payment" : `Payment ${targetIdx + 1}`),
  };
  return reconcileDepositSchedule(out, next);
}

/** Rescale scheduled amounts when invoice total changes (keeps paid fields). */
export function rescaleDepositsForPlan(deposits, amount, plan, issued) {
  const normalized = normalizePaymentPlan(plan);
  const parsed = roundMoney(amount);
  const issuedDate = (issued || "").slice(0, 10);
  if (!deposits?.length) return buildDepositsForPlan(parsed, normalized, issuedDate, issuedDate);

  if (normalized === "partial" && deposits.length >= 2) {
    const oldSum = roundMoney(deposits.reduce((s, d) => s + (Number(d.amount) || 0), 0));
    if (oldSum <= 0) {
      return buildDepositsForPlan(parsed, normalized, issuedDate, issuedDate, {
        prefillPaidDates: true,
      });
    }
    let allocated = 0;
    return deposits.map((d, i) => {
      let nextAmount;
      if (i < deposits.length - 1) {
        nextAmount = roundMoney(parsed * ((Number(d.amount) || 0) / oldSum));
        allocated += nextAmount;
      } else {
        nextAmount = roundMoney(parsed - allocated);
      }
      return {
        ...d,
        label: d.label || `Payment ${i + 1}`,
        amount: nextAmount,
        percent: parsed > 0 ? roundMoney((nextAmount / parsed) * 100) : 0,
      };
    });
  }

  const d0 = deposits[0];
  return [{ ...d0, label: "Full payment", percent: 100, amount: parsed }];
}
